import React, { useEffect } from 'react';
import { Form, Input, Modal, Select, message } from 'antd';

import ImagePasteUpload from '../../../components/ImagePasteUpload';
import SalesOrderLineItemSelect from '../../../components/SalesOrderLineItemSelect';
import type {
	ProductionQuestion,
	ProductionQuestionCategory,
	ProductionQuestionResolutionCategory,
	ProductionQuestionResolutionSource,
	ProductionQuestionStatus,
} from '../../../interfaces/ProductionQuestion.interface';
import {
	RESOLUTION_CATEGORY_OPTIONS,
	RESOLUTION_SOURCE_OPTIONS,
	SCROLLABLE_MODAL_STYLES,
	STATUS_OPTIONS,
} from './constants';
import {
	useAddProductionQuestionAttachmentsMutation,
	useCreateProductionQuestionMutation,
	useDeleteProductionQuestionAttachmentMutation,
	useUpdateProductionQuestionMutation,
} from '../../../redux/api/production-questions';

const CATEGORY_OPTIONS: { label: string; value: ProductionQuestionCategory }[] = [
	{ label: 'Rounding Error', value: 'rounding_error' },
	{ label: 'Model and Drawing Differ', value: 'model_drawing_differ' },
	{ label: 'Drawing Missing Information', value: 'drawing_missing_info' },
	{ label: 'Other', value: 'other' },
];

interface FormValues {
	order_line_item_ids: string[];
	category: ProductionQuestionCategory;
	status: ProductionQuestionStatus;
	description: string;
	resolution_category?: ProductionQuestionResolutionCategory;
	resolution_source?: ProductionQuestionResolutionSource;
	resolution_description: string;
}

interface QuestionFormModalProps {
	/** Pre-selects this line item when creating from an OLI page. */
	lineItemId?: string;
	question: ProductionQuestion | null;
	open: boolean;
	onClose: () => void;
	disabled?: boolean;
}

const resolveContextLineItemId = (
	lineItemId?: string,
	question?: ProductionQuestion | null
): string | undefined => {
	if (lineItemId) return lineItemId;
	if (question?.order_line_items?.length) {
		return question.order_line_items[0].id;
	}
	return question?.order_line_item_id ?? undefined;
};

const QuestionFormModal: React.FC<QuestionFormModalProps> = ({
	lineItemId,
	question,
	open,
	onClose,
	disabled = false,
}) => {
	const [form] = Form.useForm<FormValues>();
	const status = Form.useWatch('status', form);
	const [drawingFiles, setDrawingFiles] = React.useState<File[]>([]);
	const [modelFiles, setModelFiles] = React.useState<File[]>([]);
	const [deletingAttachmentId, setDeletingAttachmentId] = React.useState<
		string | null
	>(null);

	const [createQuestion, { isLoading: isCreating }] =
		useCreateProductionQuestionMutation();
	const [updateQuestion, { isLoading: isUpdating }] =
		useUpdateProductionQuestionMutation();
	const [addAttachments, { isLoading: isAddingAttachments }] =
		useAddProductionQuestionAttachmentsMutation();
	const [deleteAttachment] = useDeleteProductionQuestionAttachmentMutation();

	useEffect(() => {
		if (!open) return;
		setDrawingFiles([]);
		setModelFiles([]);

		const linkedIds = question?.order_line_items?.map((item) => item.id);
		form.setFieldsValue({
			order_line_item_ids:
				linkedIds ?? (lineItemId ? [lineItemId] : []),
			category: question?.category ?? 'model_drawing_differ',
			status: question?.status ?? 'not_reported',
			description: question?.description ?? '',
			resolution_category: question?.resolution_category || undefined,
			resolution_source: question?.resolution_source || undefined,
			resolution_description: question?.resolution_description ?? '',
		});
	}, [open, question, form, lineItemId]);

	const handleDeleteExisting = async (attachmentId: string) => {
		if (!question) return;
		const attachmentLineItemId = resolveContextLineItemId(
			lineItemId,
			question
		);
		if (!attachmentLineItemId) return;

		setDeletingAttachmentId(attachmentId);
		try {
			await deleteAttachment({
				lineItemId: attachmentLineItemId,
				questionId: question.id,
				attachmentId,
			}).unwrap();
		} catch {
			message.error('Failed to delete screenshot');
		} finally {
			setDeletingAttachmentId(null);
		}
	};

	const handleSubmit = async (values: FormValues) => {
		const lineItemIds = values.order_line_item_ids ?? [];
		if (!lineItemIds.length) {
			message.error('Select at least one line item');
			return;
		}

		const contextLineItemId = resolveContextLineItemId(
			lineItemId ?? lineItemIds[0],
			question
		);

		try {
			if (question) {
				if (!contextLineItemId) {
					message.error('Unable to update question');
					return;
				}

				await updateQuestion({
					lineItemId: contextLineItemId,
					questionId: question.id,
					lineItemIds,
					category: values.category,
					description: values.description,
					status: values.status,
					resolution_category:
						values.status === 'resolved'
							? values.resolution_category
							: undefined,
					resolution_source:
						values.status === 'resolved'
							? values.resolution_source
							: undefined,
					resolution_description:
						values.status === 'resolved'
							? values.resolution_description
							: undefined,
				}).unwrap();

				if (drawingFiles.length) {
					await addAttachments({
						lineItemId: contextLineItemId,
						questionId: question.id,
						kind: 'drawing',
						files: drawingFiles,
					}).unwrap();
				}
				if (modelFiles.length) {
					await addAttachments({
						lineItemId: contextLineItemId,
						questionId: question.id,
						kind: 'model',
						files: modelFiles,
					}).unwrap();
				}
				message.success('Question updated');
			} else {
				await createQuestion({
					lineItemIds,
					category: values.category,
					description: values.description,
					status: values.status,
					resolution_category:
						values.status === 'resolved'
							? values.resolution_category
							: undefined,
					resolution_source:
						values.status === 'resolved'
							? values.resolution_source
							: undefined,
					resolution_description:
						values.status === 'resolved'
							? values.resolution_description
							: undefined,
					drawingFiles,
					modelFiles,
				}).unwrap();
				message.success('Production question created');
			}
			onClose();
		} catch {
			message.error('Failed to save question');
		}
	};

	return (
		<Modal
			title={
				question
					? `Edit Production Question ${question.uid}`
					: lineItemId
					? 'New Production Question'
					: 'New Production Question'
			}
			open={open}
			onCancel={onClose}
			onOk={() => form.submit()}
			okText={question ? 'Save' : 'Create'}
			confirmLoading={
				isCreating || isUpdating || isAddingAttachments
			}
			width={820}
			centered
			destroyOnClose
			okButtonProps={{ disabled }}
			styles={SCROLLABLE_MODAL_STYLES}
		>
			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<Form.Item
					name="order_line_item_ids"
					label="Line Items"
					extra="Select batch line items from the same order with the same part name that share this question."
					rules={[
						{
							required: true,
							type: 'array',
							min: 1,
							message: 'Select at least one line item',
						},
					]}
				>
					<SalesOrderLineItemSelect
						mode="multiple"
						restrictToSameOrderAndName
						showThumbnail
						showBuilding={false}
						disabled={disabled}
					/>
				</Form.Item>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<Form.Item
						name="category"
						label="Category"
						rules={[{ required: true, message: 'Select a category' }]}
					>
						<Select options={CATEGORY_OPTIONS} disabled={disabled} />
					</Form.Item>
					<Form.Item
						name="status"
						label="Status"
						rules={[{ required: true, message: 'Select a status' }]}
					>
						<Select
							options={STATUS_OPTIONS}
							disabled={
								disabled ||
								!question ||
								question.status === 'resolved'
							}
						/>
					</Form.Item>
				</div>

				<Form.Item name="description" label="Description">
					<Input.TextArea
						rows={3}
						placeholder="Describe the question or issue"
						disabled={disabled}
					/>
				</Form.Item>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<ImagePasteUpload
						label="Drawing screenshots"
						files={drawingFiles}
						onFilesChange={setDrawingFiles}
						existingAttachments={question?.drawing_attachments}
						onDeleteExisting={
							question ? handleDeleteExisting : undefined
						}
						disabled={disabled}
						deletingAttachmentId={deletingAttachmentId}
					/>
					<ImagePasteUpload
						label="3D model screenshots"
						files={modelFiles}
						onFilesChange={setModelFiles}
						existingAttachments={question?.model_attachments}
						onDeleteExisting={
							question ? handleDeleteExisting : undefined
						}
						disabled={disabled}
						deletingAttachmentId={deletingAttachmentId}
					/>
				</div>

				{status === 'resolved' && (
					<div className="mt-4 grid grid-cols-1 gap-4">
						<Form.Item
							name="resolution_source"
							label="Resolution source"
							rules={[
								{ required: true, message: 'Select who resolved this question' },
							]}
						>
							<Select
								options={RESOLUTION_SOURCE_OPTIONS}
								placeholder="Select resolution source"
								disabled={disabled}
							/>
						</Form.Item>
						<Form.Item
							name="resolution_category"
							label="Resolution category"
							rules={[
								{ required: true, message: 'Select a resolution category' },
							]}
						>
							<Select
								options={RESOLUTION_CATEGORY_OPTIONS}
								placeholder="Select resolution category"
								disabled={disabled}
							/>
						</Form.Item>
						<Form.Item
							name="resolution_description"
							label="Resolution"
							rules={[
								{
									required: true,
									message: 'Explain how this question was resolved',
								},
							]}
						>
							<Input.TextArea
								rows={3}
								placeholder="For example: Customer verbally approved the 3D dimensions."
								disabled={disabled}
							/>
						</Form.Item>
					</div>
				)}
			</Form>
		</Modal>
	);
};

export default QuestionFormModal;
